<template>
  <div
    ref="rootEl"
    class="flow-node"
    :class="[
      `flow-node--${kind}`,
      { 'flow-node--has-warning': hasWarnings },
      { 'flow-node--user-disabled': isUserDisabled },
      { 'flow-node--selection-mode': selectionMode },
      { 'flow-node--selectable': isSelectable && !isMergeTarget },
      { 'flow-node--selected': isSelected && !isMergeTarget },
      { 'flow-node--dimmed': selectionMode && !isSelectable && !isMergeTarget },
      { 'flow-node--merge-mode': isMergeTarget },
      { 'flow-node--merge-dimmed': isMergeTarget && !isSelectable },
      { 'flow-node--highlighted': isHighlighted }
    ]"
    :style="nodeStyle"
    @mouseenter="$emit('mouseenter', node.step.id)"
    @mouseleave="$emit('mouseleave', node.step.id)"
  >
    <!-- Target bulk calculation badge (top right, outside box) -->
    <div
      v-if="targetCalculation"
      v-tooltip.top="targetCalculation === 'loading' ? '' : t('automatedScenarios.targetBulkCalculations.approximation_tooltip')"
      class="flow-node__target-badge"
      :class="{
        'flow-node__target-badge--loading': targetCalculation === 'loading',
        'flow-node__target-badge--zero':
          targetCalculation !== 'loading' && targetCalculation.in === 0,
        'flow-node__target-badge--no-estimation':
          targetCalculation !== 'loading' && targetCalculation.in === null
      }"
    >
      <template v-if="targetCalculation === 'loading'">
        <i class="fa-thin fa-spinner fa-spin" />
        <span>{{ t("loading") }}</span>
      </template>
      <template v-else-if="targetCalculation.timeout_exceeded">
        <i class="fa-regular fa-circle-exclamation" />
        <span>{{
          t("automatedScenarios.targetBulkCalculations.step_no_estimation")
        }}</span>
      </template>
      <template
        v-else-if="
          targetCalculation.in !== null && targetCalculation.in !== undefined
        "
      >
        <i class="fa-regular fa-down" />
        <span>{{
          t(
            "automatedScenarios.targetBulkCalculations.contact",
            { count: targetCalculation.in },
            targetCalculation.in
          )
        }}</span>
      </template>
      <template v-else>
        <i class="fa-regular fa-circle-exclamation" />
        <span>{{
          t("automatedScenarios.targetBulkCalculations.step_no_estimation")
        }}</span>
      </template>
    </div>

    <!-- Input connector (on top edge) - shown if node has a parent, hidden in analytics mode -->
    <div
      v-if="hasInput && !isAnalyticsMode"
      class="flow-node__input-connector"
      :class="{
        'flow-node__input-connector--merge-target':
          isMergeTarget && isSelectable,
        'flow-node__input-connector--merge-selected':
          isMergeTarget && isSelected
      }"
      @click.stop="
        isMergeTarget && isSelectable
          ? $emit('selection-toggle')
          : $emit('add-before')
      "
    >
      <i
        v-if="!isMergeTarget"
        class="far fa-plus"
      />
    </div>

    <!-- Main content -->
    <div
      class="flow-node__content"
      :class="{
        'flow-node__content--error': warningSeverity === 'error' && hasWarnings,
        'flow-node__content--warning':
          warningSeverity === 'warning' && hasWarnings,
        'flow-node__content--info': warningSeverity === 'info' && hasWarnings
      }"
      @click.stop="handleNodeClick"
    >
      <!-- Left color bar (absolute positioned) -->
      <div class="flow-node__color-bar" />

      <!-- Loading overlay -->
      <div
        v-if="isLoading"
        class="flow-node__loading-overlay"
      >
        <i class="far fa-spinner-third fa-spin" />
      </div>

      <!-- Main row with icon, body, badge, actions -->
      <div class="flow-node__main-row">
        <!-- Icon -->
        <div class="flow-node__icon">
          <i :class="`far ${icon}`" />
        </div>

        <!-- Body -->
        <div class="flow-node__body">
          <div class="flow-node__label">
            {{ label }}
          </div>
          <div
            v-if="subtitle"
            class="flow-node__subtitle"
          >
            {{ subtitle }}
          </div>
        </div>

        <!-- Persistence badge -->
        <div
          v-if="hasPersistence"
          class="flow-node__persistence-badge"
        >
          <span>P</span>
          <div class="flow-node__persistence-tooltip">
            <div class="flow-node__persistence-tooltip-title">
              {{ persistenceTitle }}
            </div>
            <div
              v-for="item in flatPersistenceList"
              :key="item"
              class="flow-node__persistence-tooltip-segment"
            >
              {{ getSegmentLabel(item) }}
            </div>
          </div>
        </div>

        <!-- Enabled operator toggle (only for automation + sending boxes, hidden in analytics mode) -->
        <div
          v-if="canToggleEnabled && !isAnalyticsMode"
          class="flow-node__enabled-toggle"
          :class="{
            'flow-node__enabled-toggle--locked': isDropdownLocked,
            'flow-node__enabled-toggle--active': enabledOperator === 1 && !isDropdownLocked,
          }"
          @click.stop="toggleEnabledDropdown"
        >
          <span
            :class="[
              'flow-node__enabled-toggle-label',
              {
                'flow-node__enabled-toggle-label--disabled':
                  enabledOperator === 0 || isDropdownLocked
              }
            ]"
          >
            {{
              enabledOperator === 1
                ? t("automatedScenarios.tooltips.enabledOperator")
                : t("automatedScenarios.tooltips.disabledOperator")
            }}
          </span>
          <i
            v-if="isDropdownLocked"
            class="far fa-lock flow-node__enabled-toggle-icon"
          />
          <i
            v-else
            class="far fa-chevron-down flow-node__enabled-toggle-icon"
          />

          <!-- Dropdown (only shown when not locked) -->
          <Transition name="menu-fade">
            <div
              v-if="enabledDropdownOpen && !isDropdownLocked"
              class="flow-node__enabled-dropdown"
            >
              <div
                class="flow-node__enabled-dropdown-item"
                :class="{
                  'flow-node__enabled-dropdown-item--active':
                    enabledOperator === 1
                }"
                @click.stop="setEnabledOperator(1)"
              >
                <i
                  v-if="enabledOperator === 1"
                  class="far fa-check"
                />
                <span>{{
                  t("automatedScenarios.tooltips.enabledOperator")
                }}</span>
              </div>
              <div
                class="flow-node__enabled-dropdown-item"
                :class="{
                  'flow-node__enabled-dropdown-item--active':
                    enabledOperator === 0
                }"
                @click.stop="setEnabledOperator(0)"
              >
                <i
                  v-if="enabledOperator === 0"
                  class="far fa-check"
                />
                <span>{{
                  t("automatedScenarios.tooltips.disabledOperator")
                }}</span>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Actions menu (three dots) - hidden in analytics mode -->
        <div
          v-if="!isAnalyticsMode"
          class="flow-node__actions"
          @click.stop="toggleMenu"
        >
          <i class="far fa-ellipsis-vertical" />
        </div>
      </div>

      <!-- Warning messages (inside the box, below main row) -->
      <div
        v-for="(warning, index) in boxWarnings"
        :key="index"
        class="flow-node__warning-bar"
        :class="`flow-node__warning-bar--${warning.severity}`"
      >
        <div class="flow-node__warning-content">
          <i class="fas fa-exclamation-triangle" />
          <span>{{
            t(warning.message, warning.messageParams || {})
          }}</span>
        </div>
        <div
          v-if="warning.actions && warning.actions.length"
          class="flow-node__warning-actions"
        >
          <button
            v-for="action in warning.actions"
            :key="action.id"
            class="flow-node__warning-action-btn"
            :disabled="isLoading"
            @click.stop="handleWarningAction(action.event)"
          >
            <i
              v-if="isLoading"
              class="far fa-spinner-third fa-spin"
            />
            <i
              v-else-if="action.icon"
              :class="action.icon"
            />
            {{ t(action.label) }}
          </button>
        </div>
      </div>

      <!-- Analytics chips (inside the box, below warning if any) -->
      <FlowAnalyticsChip
        v-if="isAnalyticsMode && operatorAnalytics"
        :operator-id="node.step.id"
        :operator-type="node.step.type"
        :is-root="isRootNode"
        :trigger-event="String(node.step.parameters?.event || '')"
        :global-stats="operatorAnalytics.globalStatistics || null"
        :send-stats="operatorAnalytics.sendStatistics || null"
        :passed-at="operatorAnalytics.passedAt || null"
        :exited-at="operatorAnalytics.exitedAt || null"
        :step-status="operatorAnalytics.stepStatus || null"
        :contact-events="operatorAnalytics.contactEvents || null"
      />
    </div>

    <!-- Dropdown menu (outside content to avoid overflow:hidden) -->
    <Transition name="menu-fade">
      <div
        v-if="menuOpen"
        class="flow-node__menu"
      >
        <!-- Normal menu items -->
        <template v-if="!confirmingDelete">
          <div
            class="flow-node__menu-item"
            @click.stop="handleEdit"
          >
            <i class="far fa-pen" />
            <span>{{ t("flows.node.edit") }}</span>
          </div>
          <div
            v-if="canReplace"
            class="flow-node__menu-item"
            @click.stop="handleReplace"
          >
            <i class="far fa-arrows-rotate" />
            <span>{{ t("flows.node.replace") }}</span>
          </div>
          <div
            v-if="outputs > 1"
            class="flow-node__menu-item"
            @click.stop="handleMenuAction('reorder')"
          >
            <i class="far fa-sort" />
            <span>{{ t("flows.node.reorder") }}</span>
          </div>
          <!-- Custom menu actions from operator -->
          <div
            v-for="action in customMenuActions"
            :key="action.id"
            class="flow-node__menu-item"
            :class="{ 'flow-node__menu-item--danger': action.danger }"
            @click.stop="handleMenuAction(action.action)"
          >
            <i :class="action.icon" />
            <span>{{ t(action.label) }}</span>
          </div>
          <div
            class="flow-node__menu-item flow-node__menu-item--danger"
            @click.stop="showDeleteConfirm"
          >
            <i class="far fa-trash" />
            <span>{{ t("flows.node.delete") }}</span>
          </div>
        </template>

        <!-- Delete confirmation or blocked message -->
        <template v-else>
          <div class="flow-node__confirm">
            <p class="flow-node__confirm-text">
              {{ deleteBlockedMessage || t("flows.node.confirmDelete") }}
            </p>
            <template v-if="!deleteBlockedMessage">
              <button
                class="flow-node__confirm-btn flow-node__confirm-btn--success"
                @click.stop="confirmDelete"
              >
                {{ t("flows.node.confirm") }}
              </button>
            </template>
            <button
              class="flow-node__confirm-btn flow-node__confirm-btn--danger"
              @click.stop="cancelDelete"
            >
              {{
                deleteBlockedMessage
                  ? t("flows.node.close")
                  : t("flows.node.cancel")
              }}
            </button>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import type { NodeWarning, WarningSeverity } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import type { FlowStructureDto } from '@/types/flow-structure';
import { getOperatorById } from '@/components/flows/operators/registry';
import FlowAnalyticsChip from '@/components/flows/FlowAnalyticsChip.vue';
import { useGlobalFlowAnalytics, OperatorAnalytics } from '@/composables/flows/useFlowAnalytics';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'FlowNode',

  components: {
    FlowAnalyticsChip,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    node: {
      type: Object as PropType<{
        step: FlowStructureDto['steps'][number];
        label: string;
        subtitle: string;
        icon: string;
        kind: string;
        left: number;
        top: number;
        hasInput: boolean;
        outputs: number;
        connectedOutputs: number[];
        warnings: NodeWarning[];
        persistenceList: Record<string, string[]>;
        effectiveHeight: number;
      }>,

      required: true,
    },

    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },

    width: {
      type: Number,
      required: true,
    },

    height: {
      type: Number,
      required: true,
    },

    targetCalculation: {
      type: [Object, String] as PropType<{ in: number | null; out_1?: number; out_2?: number; timeout_exceeded?: boolean } | 'loading' | null>,
      default: null,
    },

    // Selection mode props
    selectionMode: {
      type: Boolean,
      default: false,
    },

    isSelectable: {
      type: Boolean,
      default: false,
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    isHighlighted: {
      type: Boolean,
      default: false,
    },

    isMergeTarget: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['add-before', 'add-after', 'click', 'edit', 'replace', 'delete', 'menu-action', 'warning-action', 'toggle-enabled', 'selection-toggle', 'mouseenter', 'mouseleave', 'resize'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const rootEl = ref<HTMLElement | null>(null);
    const menuOpen = ref(false);
    const confirmingDelete = ref(false);
    const deleteBlockedMessage = ref<string | null>(null);

    // Analytics mode
    const analytics = useGlobalFlowAnalytics();
    const isAnalyticsMode = computed(() => analytics.isAnalyticsMode.value);
    const operatorAnalytics = computed((): OperatorAnalytics | null => analytics.getOperatorAnalytics(props.node.step.id));

    // Computed properties from node
    const label = computed(() => props.node.label);
    const subtitle = computed(() => props.node.subtitle);
    const icon = computed(() => props.node.icon);
    const kind = computed(() => props.node.kind);
    const hasInput = computed(() => props.node.hasInput);
    const outputs = computed(() => props.node.outputs);
    const connectedOutputs = computed(() => props.node.connectedOutputs);

    const nodeStyle = computed(() => ({
      top: `${props.node.top}px`,
      left: `${props.node.left}px`,
      width: `${props.width}px`,
    }));

    // Warning computed properties - only show 'box' warnings on the node
    const boxWarnings = computed(() => props.node.warnings.filter((w) => w.location === 'box'));
    const hasWarnings = computed(() => boxWarnings.value.length > 0);

    // Check if this node is the root (no parent in the flow)
    const isRootNode = computed(() => {
      if (!props.flow) return false;
      const stepId = props.node.step.id;
      // Root node has no connections pointing to it
      return !props.flow.connections.some((conn) => conn.childId === stepId);
    });

    // Hide replace menu for root node in bulk campaigns
    const canReplace = computed(() => {
      if (props.flow?.type === TypeCampaignEnum.BULK && isRootNode.value) {
        return false;
      }
      return true;
    });

    // Persistence computed properties
    const persistenceList = computed(() => props.node.persistenceList);
    const hasPersistence = computed(() => {
      const list = persistenceList.value;
      if (!list) return false;
      // Check if there are any non-empty arrays in the persistence list
      return Object.values(list).some((arr) => arr && arr.length > 0);
    });
    const persistenceCount = computed(() => {
      const list = persistenceList.value;
      if (!list) return 0;
      // Count total segments across all keys
      return Object.values(list).reduce((total, segments) => total + (segments?.length || 0), 0);
    });
    const persistenceTitle = computed(() => t('automatedScenarios.tooltips.persistence.title', persistenceCount.value));
    const flatPersistenceList = computed(() => {
      const list = persistenceList.value;
      if (!list) return [];
      return Object.values(list).flat();
    });

    // Get human-readable label for an operator ID (mainType)
    const getOperatorLabel = (operatorId: string): string => {
      const key = `automatedScenarios.tooltips.persistence.mainType.${operatorId}`;
      const translated = t(key);
      // If translation not found, return the operatorId as fallback
      return translated !== key ? translated : operatorId;
    };

    // Can be a translation key or a step ID from the flow
    const getSegmentLabel = (segmentId: string): string => {
      // Check if segmentId is a step ID in the current flow
      if (props.flow) {
        const step = props.flow.steps.find((s) => s.id === segmentId);
        if (step) {
          // Get the operator and use its getDisplayLabel
          const stepOperator = getOperatorById(step.type);
          if (stepOperator?.getDisplayLabel) {
            const displayLabel = stepOperator.getDisplayLabel(step.parameters || {}, t);
            if (displayLabel) return displayLabel;
          }
          // Fallback to operator label
          return stepOperator?.label ? t(stepOperator.label) : segmentId;
        }
      }

      // Fallback to translation key
      const key = `automatedScenarios.tooltips.persistence.subType.${segmentId}`;
      const translated = t(key);
      // If translation not found, return the segmentId as fallback
      return translated !== key ? translated : segmentId;
    };

    const warningSeverity = computed((): WarningSeverity => {
      if (boxWarnings.value.some((w) => w.severity === 'error')) return 'error';
      if (boxWarnings.value.some((w) => w.severity === 'warning')) return 'warning';
      return 'info';
    });

    // Get custom menu actions from operator
    const operator = computed(() => getOperatorById(props.node.step.type));
    const customMenuActions = computed(() => operator.value?.menuActions || []);

    // Enabled operator toggle (only for automation and operators with canToggleEnabled)
    const canToggleEnabled = computed(() => props.flow?.type === TypeCampaignEnum.AUTOMATION && operator.value?.canToggleEnabled === true);

    const enabledOperator = computed(() => {
      const params = props.node.step.parameters;
      if (!params) return 1;
      // Parameters can be object Record<string, string> or array [{key, value}]
      if (Array.isArray(params)) {
        const enabledParam = (params as Array<{key: string; value: string}>).find((p) => p.key === 'enabled_operator');
        return enabledParam ? parseInt(enabledParam.value, 10) : 1;
      }
      // Object format
      const value = (params as Record<string, string>).enabled_operator;
      return value !== undefined ? parseInt(value, 10) : 1;
    });

    // Tracks whether the current disabled state was set by the system (blocking
    // warnings) vs by the user. Used to auto-re-enable when the warnings clear,
    // without overwriting the user's explicit "inactive" intent.
    const systemAutoDisabled = computed(() => {
      const params = props.node.step.parameters;
      if (!params) return false;
      const raw = Array.isArray(params)
        ? (params as Array<{ key: string; value: string }>).find((p) => p.key === 'system_auto_disabled')?.value
        : (params as Record<string, string>).system_auto_disabled;
      return raw === '1' || raw === 'true';
    });

    // Check if there are blocking warnings (prevents enabling)
    const hasBlockingWarnings = computed(() => boxWarnings.value.some((w) => w.blocking === true));

    // Auto-disable / auto-re-enable based on blocking warnings.
    // system_auto_disabled distinguishes system-triggered disable (re-enable when
    // the warning clears) from user-triggered disable (stays disabled).
    watch(hasBlockingWarnings, (hasBlocking) => {
      if (hasBlocking && enabledOperator.value === 1) {
        emit('toggle-enabled', {
          stepId: props.node.step.id,
          enabled: 0,
          systemAutoDisabled: true,
        });
      } else if (!hasBlocking && enabledOperator.value === 0 && systemAutoDisabled.value) {
        emit('toggle-enabled', {
          stepId: props.node.step.id,
          enabled: 1,
          systemAutoDisabled: false,
        });
      }
    }, { immediate: true });

    // Dropdown is locked when there are blocking warnings
    const isDropdownLocked = computed(() => hasBlockingWarnings.value);

    // Visual dim when the box is disabled by the user (not by the system).
    // System-disabled boxes already show a red warning banner; user-disabled
    // boxes otherwise look identical to active ones, so we dim the content.
    // Restricted to operators the user can actually toggle (automation +
    // metadata flag) — otherwise a stale `enabled_operator: 0` on a bulk
    // trigger would dim a box the user has no way to re-enable.
    const isUserDisabled = computed(() => canToggleEnabled.value
        && enabledOperator.value === 0
        && !hasBlockingWarnings.value);

    const enabledDropdownOpen = ref(false);

    const toggleEnabledDropdown = (e: Event) => {
      e.stopPropagation();
      if (props.isLoading) return;
      // Don't open dropdown if locked
      if (isDropdownLocked.value) return;
      enabledDropdownOpen.value = !enabledDropdownOpen.value;
    };

    const setEnabledOperator = (value: number) => {
      enabledDropdownOpen.value = false;
      // User action: clear the auto-disable flag so the system won't later
      // re-enable a box the user explicitly turned off, and won't forget the
      // explicit intent when the user re-enables.
      emit('toggle-enabled', {
        stepId: props.node.step.id,
        enabled: value,
        systemAutoDisabled: false,
      });
    };

    const toggleMenu = () => {
      if (props.isLoading) return;
      menuOpen.value = !menuOpen.value;
      if (!menuOpen.value) {
        confirmingDelete.value = false;
      }
    };

    const closeMenu = () => {
      menuOpen.value = false;
      confirmingDelete.value = false;
    };

    const handleEdit = () => {
      closeMenu();
      emit('click');
    };

    const handleReplace = () => {
      closeMenu();
      emit('replace');
    };

    const handleMenuAction = (actionId: string) => {
      closeMenu();
      emit('menu-action', actionId, props.node.step);
    };

    const handleWarningAction = (event: string) => {
      emit('warning-action', event, props.node.step);
    };

    const showDeleteConfirm = async () => {
      // Check if deletion is allowed
      deleteBlockedMessage.value = null;
      const operator = getOperatorById(props.node.step.type);
      if (operator?.canBeDeleted && props.flow) {
        const result = await operator.canBeDeleted(props.node.step.id, props.flow);
        if (result !== true) {
          deleteBlockedMessage.value = typeof result === 'string' ? result : t('automatedScenarios.cant_remove_operator');
        }
      }
      confirmingDelete.value = true;
    };

    const cancelDelete = () => {
      confirmingDelete.value = false;
    };

    const confirmDelete = () => {
      closeMenu();
      emit('delete');
    };

    // Handle node click - in selection mode, toggle selection; otherwise emit click
    // In analytics mode, do nothing (no editing allowed)
    // Deprecated / not-available-here operators stay clickable so users can
    // open the form, inspect what was configured, and migrate to the
    // replacement filter before deleting.
    const handleNodeClick = () => {
      if (props.isLoading) return;
      if (isAnalyticsMode.value) {
        // Do nothing in analytics mode
        return;
      }
      if (props.selectionMode) {
        if (props.isSelectable) {
          emit('selection-toggle');
        }
        // Do nothing if not selectable in selection mode
      } else {
        emit('click');
      }
    };

    // Close menu and enabled dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.flow-node__actions') && !target.closest('.flow-node__menu')) {
        closeMenu();
      }
      if (!target.closest('.flow-node__enabled-toggle')) {
        enabledDropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);

      // Monitor size changes (especially for analytics chips expansion)
      if (rootEl.value) {
        const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
          for (const entry of entries) {
            // Use borderBoxSize if available for better accuracy, fall back to offsetHeight
            let height = 0;
            if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
              height = entry.borderBoxSize[0].blockSize;
            } else {
              height = (entry.target as HTMLElement).offsetHeight;
            }

            // Emit resize event
            emit('resize', {
              stepId: props.node.step.id,
              height,
            });
          }
        });
        resizeObserver.observe(rootEl.value);

        // Cleanup observer on unmount
        onBeforeUnmount(() => {
          resizeObserver.disconnect();
          document.removeEventListener('click', handleClickOutside);
        });
      }
    });

    onBeforeUnmount(() => {
      // document listener removed in resize cleanup block if initialized, acting as backup
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      t,
      label,
      subtitle,
      icon,
      kind,
      hasInput,
      outputs,
      connectedOutputs,
      nodeStyle,
      menuOpen,
      confirmingDelete,
      deleteBlockedMessage,
      toggleMenu,
      handleEdit,
      handleReplace,
      canReplace,
      handleMenuAction,
      handleWarningAction,
      showDeleteConfirm,
      cancelDelete,
      confirmDelete,
      // Warnings
      boxWarnings,
      hasWarnings,
      warningSeverity,
      isRootNode,
      // Persistence
      persistenceList,
      hasPersistence,
      persistenceTitle,
      flatPersistenceList,
      getSegmentLabel,
      // Custom menu actions
      customMenuActions,
      // Target calculation
      targetCalculation: computed(() => props.targetCalculation),
      // Enabled operator toggle
      canToggleEnabled,
      enabledOperator,
      enabledDropdownOpen,
      toggleEnabledDropdown,
      setEnabledOperator,
      isDropdownLocked,
      isUserDisabled,
      // Selection mode
      handleNodeClick,
      selectionMode: computed(() => props.selectionMode),
      isSelectable: computed(() => props.isSelectable),
      isSelected: computed(() => props.isSelected),
      // Analytics mode
      isAnalyticsMode,
      operatorAnalytics,
      rootEl,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-node {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;

  // Main content box - rectangular design
  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    width: 100%;
    min-height: 60px; // Minimum height to match BOX_HEIGHT in FlowRenderer
    box-sizing: border-box;
    cursor: pointer;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      border-color: #d1d5db;
    }
  }

  // Main row inside content (icon, body, badge, actions)
  &__main-row {
    display: flex;
    align-items: center;
    height: 60px;
    flex-shrink: 0;
  }

  // Left color bar (spans full height of content box)
  &__color-bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 0.5rem 0 0 0.5rem;
  }

  &__loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 0.5rem;
    z-index: 5;

    i {
      font-size: 1.5rem;
      color: #9ca3af;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0.75rem 0.625rem 0.75rem calc(4px + 1rem); // 4px for color bar + margin
    border-radius: 0.5rem;
    flex-shrink: 0;

    i {
      font-size: 1rem;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    min-width: 0;
    padding: 0.5rem 0;
  }

  &__label {
    font-weight: 600;
    font-size: 0.8125rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    color: #6b7280;
    font-size: 0.6875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 0.125rem;
  }

  // Enabled operator toggle
  &__enabled-toggle {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    margin-right: 0.25rem;
    border-radius: 0.25rem;
    background: #f3f4f6;
    cursor: pointer;
    transition: background 0.15s ease;
    font-size: 0.6875rem;

    &:hover {
      background: #e5e7eb;
    }

    &--locked {
      cursor: not-allowed;
    }

    // Active state: pastel blue
    &--active {
      background: #dbeafe;

      &:hover {
        background: #bfdbfe;
      }
    }
  }

  &__enabled-toggle-label {
    color: #374151;
    font-weight: 500;

    &--disabled {
      color: #9ca3af;
    }
  }

  &__enabled-toggle-icon {
    font-size: 0.5rem;
    color: #6b7280;
  }

  &__enabled-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    background: #fff;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e7eb;
    min-width: 120px;
    z-index: 110;
    overflow: hidden;
  }

  &__enabled-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      color: #374151;
      font-weight: 500;
    }

    &--disabled {
      color: #9ca3af;
      cursor: not-allowed;

      &:hover {
        background: transparent;
      }
    }

    i {
      font-size: 0.625rem;
      width: 12px;
    }
  }

  // Actions menu (three dots)
  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 100%;
    cursor: pointer;
    color: #9ca3af;
    transition: color 0.15s ease;
    padding: 0 0.25rem;

    &:hover {
      color: #4b5563;
    }

    i {
      font-size: 0.875rem;
    }
  }

  // Dropdown menu (positioned to the right of the box)
  &__menu {
    position: absolute;
    top: 0;
    left: 100%; // Position to the right of the box
    margin-left: 0.5rem;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e7eb;
    min-width: 160px;
    z-index: 100;
  }

  // Menu animation
  .menu-fade-enter-active,
  .menu-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  .menu-fade-enter-from,
  .menu-fade-leave-to {
    opacity: 0;
    transform: translateX(-8px);
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s ease;
    white-space: nowrap;

    &:hover {
      background: #f3f4f6;
    }

    i {
      font-size: 0.75rem;
      width: 16px;
      text-align: center;
    }

    &--danger {
      color: #dc2626;

      &:hover {
        background: #fef2f2;
      }
    }
  }

  // Delete confirmation
  &__confirm {
    padding: 0.75rem;
    min-width: 200px;
  }

  &__confirm-text {
    margin: 0 0 0.75rem 0;
    font-size: 0.8125rem;
    color: #374151;
    line-height: 1.4;
  }

  &__confirm-btn {
    display: block;
    width: 100%;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.9;
    }

    &--success {
      background: #84cc16;
      color: #fff;
      margin-bottom: 0.5rem;
    }

    &--danger {
      background: #dc2626;
      color: #fff;
    }
  }

  // Input connector (positioned at top edge of box, half inside half outside)
  &__input-connector {
    position: absolute;
    top: -11px; // Half of button height (22px / 2 = 11px) so it sits on the edge
    left: 50%;
    transform: translateX(-50%);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #d1d5db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease,
      border-color 0.15s ease;
    z-index: 10;

    i {
      color: #6b7280;
      font-size: 0.65rem;
      font-weight: 600;
    }

    &:hover {
      transform: translateX(-50%) scale(1.1);
      border-color: #9ca3af;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
    }

    // Merge target state - orange highlight like old system
    &--merge-target {
      background: #ffc56e;
      border-color: #ffc56e;
      box-shadow: 0 0 0 3px rgba(255, 197, 110, 0.4),
        0 3px 8px rgba(0, 0, 0, 0.15);
      animation: pulse-merge 1.5s infinite;
      cursor: pointer;

      &:hover {
        transform: translateX(-50%) scale(1.2);
        box-shadow: 0 0 0 4px rgba(255, 197, 110, 0.6),
          0 4px 12px rgba(0, 0, 0, 0.2);
      }
    }

    // Merge selected state - green highlight
    &--merge-selected {
      background: #93c840;
      border-color: #93c840;
      box-shadow: 0 0 0 3px rgba(147, 200, 64, 0.4),
        0 3px 8px rgba(0, 0, 0, 0.15);
      animation: none;

      &:hover {
        transform: translateX(-50%) scale(1.2);
        box-shadow: 0 0 0 4px rgba(147, 200, 64, 0.6),
          0 4px 12px rgba(0, 0, 0, 0.2);
      }
    }
  }

  @keyframes pulse-merge {
    0%,
    100% {
      box-shadow: 0 0 0 3px rgba(255, 197, 110, 0.4),
        0 3px 8px rgba(0, 0, 0, 0.15);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(255, 197, 110, 0.2),
        0 3px 8px rgba(0, 0, 0, 0.15);
    }
  }

  // Severity-based border colors
  &__content--error {
    border-color: #fca5a5 !important;

    &:hover {
      border-color: #f87171 !important;
    }
  }

  &__content--warning {
    border-color: #fcd34d !important;

    &:hover {
      border-color: #fbbf24 !important;
    }
  }

  &__content--info {
    border-color: #93c5fd !important;

    &:hover {
      border-color: #60a5fa !important;
    }
  }

  // Warning bar (inline message inside the box, aligned with icon block)
  &__warning-bar {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    margin: 0 0.75rem 0.5rem calc(4px + 1rem); // top, right, bottom, left (aligned with icon)
    font-size: 0.75rem;
    box-sizing: border-box;

    &--error {
      background: #fef2f2;

      i {
        color: #dc2626;
      }
    }

    &--warning {
      background: #fffbeb;

      i {
        color: #d97706;
      }
    }

    &--info {
      background: #eff6ff;

      i {
        color: #2563eb;
      }
    }
  }

  &__warning-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      font-size: 0.875rem;
      flex-shrink: 0;
    }

    span {
      line-height: 1.3;
      color: #374151; // dark gray text
    }
  }

  &__warning-actions {
    display: flex;
    gap: 0.375rem;
    margin-top: 0.375rem;
    flex-basis: 100%;
  }

  &__warning-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 600;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.08);
    color: #374151;
    transition: background 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    i {
      font-size: 0.625rem;
    }
  }

  // Kind-specific styles
  &--trigger {
    .flow-node__color-bar {
      background: #f59e0b;
    }

    .flow-node__icon {
      background: #fef3c7;
      color: #d97706;
    }
  }

  &--action {
    .flow-node__color-bar {
      background: #22c55e;
    }

    .flow-node__icon {
      background: #dcfce7;
      color: #16a34a;
    }
  }

  &--filter {
    .flow-node__color-bar {
      background: #3b82f6;
    }

    .flow-node__icon {
      background: #dbeafe;
      color: #2563eb;
    }
  }

  // Persistence badge (inside the box)
  &__persistence-badge {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: #ede9fe;
    color: #7c3aed;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    cursor: default;
    flex-shrink: 0;
    margin-right: 0.5rem;

    &:hover .flow-node__persistence-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(0);
    }
  }

  &__persistence-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    background: #1f2937;
    color: #fff;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.75rem;
    font-weight: 400;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    text-align: left;

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #1f2937;
    }

    &-title {
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #fff;
    }

    &-operator {
      margin-bottom: 0.25rem;

      &:last-child {
        margin-bottom: 0;
      }

      &-name {
        display: block;
        color: #e5e7eb;

        &::before {
          content: "- ";
        }
      }
    }

    &-segment {
      color: #d1d5db;
      padding-left: 1rem;

      &::before {
        content: "* ";
      }
    }
  }

  // Target bulk calculation badge (positioned outside the box, top right)
  &__target-badge {
    position: absolute;
    top: -1.75rem;
    right: 0;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background-color: #f8f9fa;
    border: 1px solid #49505742;
    border-radius: 3px;
    font-size: 0.75rem;
    color: #374151;
    white-space: nowrap;
    z-index: 5;

    i {
      font-size: 0.75rem;
    }

    small {
      font-size: 0.625rem;
    }

    // Loading state
    &--loading {
      color: #6b7280;
    }

    // Zero contacts - warning style
    &--zero {
      border-color: #f4ada5;
      color: #dc2626;

      i {
        color: #f4ada5;
      }
    }

    // No estimation - info style
    &--no-estimation {
      border-color: #7bbffc;
      color: #2563eb;

      i {
        color: #7bbffc;
      }
    }
  }

  // Selection mode styles
  &--selection-mode {
    // Disable normal hover effects in selection mode
    .flow-node__content:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border-color: #e5e7eb;
    }

    // Hide warnings in selection mode
    .flow-node__warning-bar {
      display: none;
    }
  }

  &--dimmed {
    opacity: 0.25;
    pointer-events: none;
    filter: grayscale(100%);

    .flow-node__content {
      cursor: not-allowed;
      background: #f3f4f6;
    }

    // Hide all interactive elements
    .flow-node__actions,
    .flow-node__input-connector,
    .flow-node__warning-bar,
    .flow-node__enabled-toggle,
    .flow-node__persistence-badge {
      display: none;
    }
  }

  // User-disabled: dim the box so the inactive state is visually obvious,
  // while keeping the Active/Inactive dropdown fully readable so the user
  // can re-activate it easily.
  &--user-disabled {
    .flow-node__icon,
    .flow-node__body,
    .flow-node__persistence-badge,
    .flow-node__color-bar {
      opacity: 0.5;
    }
  }

  &--selectable {
    z-index: 100;

    .flow-node__content,
    .flow-node__content--error,
    .flow-node__content--warning,
    .flow-node__content--info {
      cursor: pointer;
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5),
        0 8px 24px rgba(59, 130, 246, 0.3) !important;
      border-color: #3b82f6 !important;
      border-width: 2px;
      animation: pulse-selection 1.5s infinite;
      transform: scale(1.02);
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 5px rgba(59, 130, 246, 0.6),
          0 10px 30px rgba(59, 130, 246, 0.35) !important;
        border-color: #2563eb !important;
        transform: scale(1.04);
      }
    }

    // Hide interactive elements but keep content visible
    .flow-node__actions,
    .flow-node__input-connector,
    .flow-node__warning-bar {
      display: none;
    }

    // Add "click to select" indicator
    &::before {
      content: "";
      position: absolute;
      inset: -8px;
      border: 2px dashed rgba(59, 130, 246, 0.4);
      border-radius: 12px;
      pointer-events: none;
    }
  }

  &--selected {
    z-index: 101;

    .flow-node__content,
    .flow-node__content--error,
    .flow-node__content--warning,
    .flow-node__content--info {
      box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.5),
        0 8px 24px rgba(148, 200, 64, 0.3) !important;
      border-color: #94c840 !important;
      border-width: 2px;
      background: linear-gradient(
        135deg,
        rgba(148, 200, 64, 0.08),
        rgba(148, 200, 64, 0.02)
      );
      transform: scale(1.02);
      animation: none;

      &:hover {
        box-shadow: 0 0 0 5px rgba(148, 200, 64, 0.6),
          0 10px 30px rgba(148, 200, 64, 0.35) !important;
        border-color: #7fb835 !important;
        transform: scale(1.04);
      }
    }

    // Hide interactive elements
    .flow-node__actions,
    .flow-node__input-connector,
    .flow-node__warning-bar {
      display: none;
    }

    // Add checkmark indicator
    &::after {
      content: "\f00c";
      font-family: "Font Awesome 6 Pro";
      font-weight: 900;
      position: absolute;
      top: -12px;
      right: -12px;
      width: 28px;
      height: 28px;
      background: linear-gradient(135deg, #94c840, #7fb835);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      box-shadow: 0 4px 12px rgba(148, 200, 64, 0.4);
      z-index: 10;
      border: 3px solid white;
    }

    // Selected dashed border
    &::before {
      content: "";
      position: absolute;
      inset: -8px;
      border: 2px dashed rgba(148, 200, 64, 0.5);
      border-radius: 12px;
      pointer-events: none;
    }
  }

  // Highlighted state - when hovering over a filter with linked operators
  // Uses a softer blue to indicate relationship without being as strong as selection
  &--highlighted {
    z-index: 102;

    .flow-node__content,
    .flow-node__content--error,
    .flow-node__content--warning,
    .flow-node__content--info {
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4),
        0 6px 20px rgba(59, 130, 246, 0.2) !important;
      border-color: #60a5fa !important;
      border-width: 2px;
      transform: scale(1.01);
      transition: all 0.2s ease;
    }
  }

  // Merge mode - only input connector is highlighted, not the whole box
  &--merge-mode {
    // Hide menu and other interactive elements, keep box normal
    .flow-node__actions,
    .flow-node__warning-bar {
      display: none;
    }
  }

  // Merge mode dimmed - nodes that are not valid merge targets
  &--merge-dimmed {
    opacity: 0.4;
    filter: grayscale(50%);

    .flow-node__content {
      cursor: not-allowed;
    }

    .flow-node__input-connector {
      display: none;
    }

    .flow-node__actions,
    .flow-node__warning-bar {
      display: none;
    }
  }

  // Analytics chips container - inside the content box
  :deep(.flow-analytics-chips) {
    padding: 0 0.75rem 0.625rem calc(4px + 0.75rem);
  }
}

@keyframes pulse-selection {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5),
      0 8px 24px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3),
      0 8px 24px rgba(59, 130, 246, 0.2);
  }
}
</style>
